import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaFolderOpen }    from '@react-icons/all-files/fa/FaFolderOpen';
import { FaProjectDiagram } from '@react-icons/all-files/fa/FaProjectDiagram';
import { FaCog }            from '@react-icons/all-files/fa/FaCog';
import { FaHome }           from '@react-icons/all-files/fa/FaHome';
import Breadcrumb from '../../../../commons/Breadcrumb';
import PageHeader from '../../../../commons/PageHeader';
import PageForm, { FormField, SelectOption } from '../../../../commons/PageForm';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { getEntity, createEntity, updateEntity, reset } from '../../../../manager/categoria-actividad.reducer';
import axios from 'axios';

const BACK = '/configuracion/workflow/categoria-actividad';

interface CategoriaActividadForm {
  id:     string;
  descripcion: string;
  tipoActividad: any;
}

const empty: CategoriaActividadForm = { id: '', descripcion: '', tipoActividad: null };



export default function CategoriaActividadFormPage() {
  const { id }   = useParams<{ id: string }>();
  const isNew    = !id;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const entity        = useAppSelector(s => s.categoriaActividad.entity) as any;
  const updating      = useAppSelector(s => s.categoriaActividad.updating);
  const updateSuccess = useAppSelector(s => s.categoriaActividad.updateSuccess);

  const [form, setForm]     = useState<CategoriaActividadForm>(empty);
  const [errors, setErrors] = useState<Partial<Record<keyof CategoriaActividadForm, string>>>({});
  const [claseOptions, setClaseOptions] = useState<SelectOption[]>([]);

  useEffect(() => {
    if (!isNew) {
      dispatch(getEntity(id!));
    } else {
      dispatch(reset());
      setForm(empty);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isNew && entity?.id) {
      setForm({
        id:     String(entity.id),
        descripcion: entity.descripcion ?? '',
        tipoActividad: entity.tipoActividad?.id != null ? String(entity.tipoActividad.id) : '',
      });
    }
  }, [entity]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      navigate(BACK, {
        state: {
          successMsg: isNew
            ? 'Categoría de actividad creada correctamente.'
            : 'Categoría de actividad actualizada correctamente.',
        },
      });
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    axios
      .get('api/tipo-actividad?page=0&size=999&sort=descripcion,asc')
      .then(res => {
        const opts: SelectOption[] = (res.data as any[]).map(c => ({
          value: String(c.id),
          label: `${c.descripcion}`,
        }));
        setClaseOptions(opts);
      })
      .catch(() => setClaseOptions([]));
  }, []);

  const handleFieldChange = (name: keyof CategoriaActividadForm, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Partial<Record<keyof CategoriaActividadForm, string>> = {};
    if (!form.descripcion.trim()) errs.descripcion = 'El nombre es requerido';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const payload = {
      ...(isNew ? {} : { id: Number(form.id) }),
      descripcion: form.descripcion,
      ...(form.tipoActividad ? { tipoActividad: { id: Number(form.tipoActividad) } } : {}),
    };

    if (isNew) {
      dispatch(createEntity(payload));
    } else {
      dispatch(updateEntity(payload));
    }
  };

  const fields: FormField<CategoriaActividadForm>[] = [
    {
      name:     'id',
      label:    'ID',
      readOnly: true,
      editOnly: true,
    },
    {
      name:        'descripcion',
      label:       'Descripción',
      required:    true,
      placeholder: 'Descripción de la categoría de actividad',
    },
    {
      name:        'tipoActividad',
      label:       'Tipo Actividad',
      required:    true,
      placeholder: 'Tipo Actividad',
      type:    'select',
      options: claseOptions,
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Breadcrumb items={[
        { label: 'Inicio',                 to: '/',                       icon: <FaHome /> },
        { label: 'Configuración',          to: '/configuracion',          icon: <FaCog /> },
        { label: 'Workflow',               to: '/configuracion/workflow', icon: <FaProjectDiagram /> },
        { label: 'Categoría de Actividad', to: BACK,                      icon: <FaFolderOpen /> },
        { label: isNew ? 'Nueva Categoría' : `Editar Categoría #${id}` },
      ]} />

      <PageHeader
        icon={<FaFolderOpen />}
        iconColor="#f59e0b"
        title={isNew ? 'Nueva Categoría de Actividad' : 'Editar Categoría de Actividad'}
        subtitle={isNew
          ? 'Completa los campos para crear una nueva categoría de actividad'
          : `Modificando categoría de actividad #${id}`}
      />

      <PageForm
        fields={fields}
        values={form}
        errors={errors}
        isNew={isNew}
        saving={updating}
        onFieldChange={handleFieldChange}
        onSubmit={handleSubmit}
        onCancel={() => navigate(BACK)}
        accentColor="#f59e0b"
      />
    </div>
  );
}
